import axios from "axios";
import router from "@/router";
import { auth } from "@/config/host.config";
import { AccessToken } from "../accessDecoder";
import { notifyServerAlive } from "@/services/connectivity";

const REQUEST_TIMEOUT_MS = 10000;

const axiosApiInstance = axios.create({ timeout: REQUEST_TIMEOUT_MS });
let refreshTokenPromise: Promise<{
  access: string;
  refreshtoken: string;
}> | null = null;

const REFRESH_AHEAD_MS = 5000;
const REFRESH_RETRY_MS = 10000;
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

export function clearProactiveRefresh(): void {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}

/** Планирует обновление токена заранее (до истечения) */
export function scheduleProactiveRefresh(): void {
  clearProactiveRefresh();
  const payload = AccessToken.payload;
  if (!payload || !payload.exp) return;

  const delay = payload.exp * 1000 - Date.now() - REFRESH_AHEAD_MS;
  if (delay <= 0) {
    refreshAccessToken().catch(() => undefined);
    return;
  }

  proactiveRefreshTimer = setTimeout(() => {
    refreshAccessToken().catch(() => undefined);
  }, delay);
}

export async function refreshAccessToken() {
  // Если уже выполняется запрос на обновление, ждем его
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = (async () => {
    try {
      const refreshRes = await axios.post(
        auth.refresh,
        {
          refreshtoken: AccessToken.refreshToken,
        },
        { timeout: REQUEST_TIMEOUT_MS }
      );
      if (refreshRes.status === 200) {
        AccessToken.setAccessToken = refreshRes.data.access;
        AccessToken.setRefreshToken = refreshRes.data.refreshtoken;
        scheduleProactiveRefresh();
        return refreshRes.data;
      }
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      // Сервер явно отверг refresh-токен — сессия невалидна.
      if (status === 400 || status === 401) {
        clearProactiveRefresh();
        AccessToken.deleteAccessToken();
        AccessToken.deleteRefreshToken();
        router.replace({ name: "login" });
      } else {
        // Сетевой сбой / 429 / 5xx — сессию сохраняем и пробуем ещё раз позже.
        clearProactiveRefresh();
        proactiveRefreshTimer = setTimeout(() => {
          refreshAccessToken().catch(() => undefined);
        }, REFRESH_RETRY_MS);
      }
      throw error;
    } finally {
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
}

// Request interceptor for API calls
axiosApiInstance.interceptors.request.use(
  (request) => {
    // Если не авторизован — запрос не отправляем вовсе
    if (!AccessToken.isAuthorized) {
      return Promise.reject(new Error("User is not authorized"));
    }
    if (request.headers) {
      request.headers.Authorization = `Bearer ${AccessToken.access}`;
    }
    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosApiInstance.interceptors.response.use(
  (response) => {
    // Успешный ответ — сервер доступен: сообщаем супервизору связности
    notifyServerAlive();
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const tokens = await refreshAccessToken();

        // Обновляем заголовок для текущего запроса
        originalRequest.headers.Authorization = `Bearer ${tokens?.access}`;

        // Повторяем оригинальный запрос
        return axiosApiInstance(originalRequest);
      } catch (refreshError) {
        // Если обновление токена не удалось, прерываем цепочку
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosApiInstance;
